import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DropCard from "@/components/DropCard";
import { Loader } from "@/components/common";

const FILTERS = [
  { key: "ACTIVE", label: "Active", dot: "bg-emerald-400" },
  { key: "FULL", label: "Full", dot: "bg-amber-400" },
  { key: "DRAWN", label: "Drawn", dot: "bg-slate-400" },
];

export default function DropsFeed() {
  const [filter, setFilter] = useState("ACTIVE");
  const [drops, setDrops] = useState(null);
  const [counts, setCounts] = useState({ ACTIVE: 0, FULL: 0, DRAWN: 0 });

  useEffect(() => {
    setDrops(null);
    api.get("/drops", { params: { status: filter } }).then(({ data }) => setDrops(data)).catch(() => setDrops([]));
  }, [filter]);

  // Refresh when the app is reopened/refocused so newly added drops show up (PWA stays in memory).
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      api.get("/drops", { params: { status: filter } }).then(({ data }) => setDrops(data)).catch(() => {});
      api.get("/drops/counts").then(({ data }) => setCounts(data)).catch(() => {});
    };
    document.addEventListener("visibilitychange", refresh);
    window.addEventListener("focus", refresh);
    return () => { document.removeEventListener("visibilitychange", refresh); window.removeEventListener("focus", refresh); };
  }, [filter]);

  useEffect(() => {
    api.get("/drops/counts").then(({ data }) => setCounts(data)).catch(() => {});
  }, [drops]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-black text-white">Drops</h1>
          <p className="text-slate-400 mt-1">Grab a seat. Watch the live draw. Win the card.</p>
        </div>
        <div className="grid grid-cols-3 gap-2 w-full sm:flex sm:w-auto sm:gap-3" data-testid="drops-filters">
          {FILTERS.map((f) => {
            const on = filter === f.key;
            return (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key.toLowerCase()}`}
                className={`ticket-tab flex items-center justify-center gap-1.5 px-2 sm:gap-2.5 sm:pl-3 sm:pr-5 py-2.5 rounded-lg font-mono text-[11px] sm:text-xs font-bold tracking-[0.08em] sm:tracking-[0.15em] uppercase border transition-[background,color,border-color,box-shadow] ${on ? "gold-gradient text-obsidian border-transparent gold-glow" : "card-surface text-slate-300 border-gold/20 hover:border-gold/50"}`}>
                <span className={`w-2 h-2 rounded-full shrink-0 ${on ? "bg-obsidian" : f.dot}`} />
                <span className={`ticket-perf h-4 hidden sm:inline-block ${on ? "text-obsidian/40" : "text-gold/40"}`} />
                {f.label}
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-md text-[10px] ${on ? "bg-obsidian/20" : "bg-gold/15 text-gold"}`} data-testid={`count-${f.key.toLowerCase()}`}>{counts[f.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
      </div>

      {drops === null ? <Loader label="Loading drops" /> : drops.length === 0 ? (
        <div className="card-surface rounded-2xl p-16 text-center text-slate-400" data-testid="drops-empty">No {filter.toLowerCase()} drops right now.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-6" data-testid="drops-grid">
          {drops.map((d, i) => <DropCard key={d.id} drop={d} index={i} />)}
        </div>
      )}
    </div>
  );
}
